import React from "react";
import { useNavigate } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import { Plus, Search, X, Upload, UserX2 } from "lucide-react";

import avatar from "assets/images/users/user-2.jpg";

import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "components/ui/avatar";
import { Skeleton } from "components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "components/ui/tooltip";
import { cn } from "lib/utils";

interface ContactListProps {
  contacts: any[];
  loading: boolean;
  count: number;
  hasMore: boolean;
  onContactSelect: (contact: any) => void;
  onAddContact: () => void;
  onSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClearSearch: () => void;
  onLoadMore: () => void;
  searchContactText: string;
  selectedChatId: string | null;
}

const initials = (name?: string) =>
  (name ?? "U")
    .split(" ")
    .map((w: string) => w.charAt(0).toUpperCase())
    .join("")
    .slice(0, 2);

const ContactList: React.FC<ContactListProps> = ({
  contacts,
  loading,
  count,
  hasMore,
  onContactSelect,
  onAddContact,
  onSearch,
  onClearSearch,
  onLoadMore,
  searchContactText,
  selectedChatId,
}) => {
  const navigate = useNavigate();

  return (
    <div className="flex h-full flex-col bg-slate-100">
      {/* Header */}
      <div className="space-y-3 border-b border-border/60 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold tracking-tight text-foreground">
              Contacts
            </h2>
            <Badge variant="soft">{count}</Badge>
          </div>
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate("/contact/import")}
                  aria-label="Import contacts"
                >
                  <Upload className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Import contacts</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onAddContact}
                  aria-label="Add contact"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Add contact</TooltipContent>
            </Tooltip>
          </div>
        </div>

        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search by name or email"
            autoComplete="off"
            value={searchContactText}
            onChange={onSearch}
            className="h-9 pl-8 pr-8 text-sm"
          />
          {searchContactText && (
            <button
              type="button"
              onClick={onClearSearch}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-hidden">
        <div className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Contact List
        </div>

        {loading && contacts.length === 0 ? (
          <div className="space-y-3 p-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-9 w-9 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3 w-2/3" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : !contacts.length ? (
          <div className="flex h-40 flex-col items-center justify-center px-4 text-center">
            <UserX2 className="mb-2 h-8 w-8 text-muted-foreground/60" />
            <p className="text-sm font-medium text-foreground">
              No contacts found
            </p>
            <p className="text-xs text-muted-foreground">
              Try a different keyword or add a new contact.
            </p>
          </div>
        ) : (
          <InfiniteScroll
            dataLength={contacts.length}
            next={onLoadMore}
            hasMore={hasMore}
            loader={
              <div className="p-3 text-center text-xs text-muted-foreground">
                Loading more…
              </div>
            }
            height={"calc(100vh - 250px)"}
            style={{ minWidth: "100%" }}
            scrollThreshold={0.9}
            scrollableTarget=""
          >
            <ul className="flex flex-col gap-0.5 px-2 pb-4">
              {contacts.map((item: any, key: number) => {
                const isActive = selectedChatId === item.id;
                return (
                  <li key={item.id ?? key}>
                    <button
                      type="button"
                      onClick={() => onContactSelect(item)}
                      className={cn(
                        "group relative flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors",
                        isActive
                          ? "bg-primary/10"
                          : "hover:bg-muted/60"
                      )}
                    >
                      {isActive && (
                        <span
                          aria-hidden
                          className="absolute inset-y-1.5 left-0 w-1 rounded-r-full bg-gradient-to-b from-primary to-accent"
                        />
                      )}
                      <Avatar className="h-9 w-9 shrink-0">
                        {item?.image ? (
                          <AvatarImage src={avatar} alt={item.name} />
                        ) : null}
                        <AvatarFallback>{initials(item.name)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p
                          className={cn(
                            "truncate text-sm",
                            isActive
                              ? "font-semibold text-foreground"
                              : "font-medium text-foreground"
                          )}
                        >
                          {item.name}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">
                          {item.email ||
                            item.phoneNumber ||
                            item.designation ||
                            "No additional info"}
                        </p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </InfiniteScroll>
        )}
      </div>
    </div>
  );
};

export default ContactList;
